import { useState } from "react";
import { toast } from "sonner";
import { X, CheckCircle2 } from "lucide-react";
import { api, resolveUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import TurnstileWidget from "@/components/TurnstileWidget";

const DEFAULT_PLACEMENT = "Wird gemeinsam abgestimmt";
const DEFAULT_SIZE = "Mindestens handflächengroß; finale Größe wird gemeinsam abgestimmt";
const SMALL_SIZE = "Kleine, bewusst reduzierte Tattoo-Version; finale Größe wird gemeinsam abgestimmt";
const SMALL_EXAMPLE_SRC = "/small-tattoo-example.webp";
const SMALL_PRICE_RANGE = "150–300 €";

export default function TattooRequestModal({ open, onClose, setData, idx, selectedSlots, combo, onSuccess, requestMode = "standard" }) {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", message: "", consent: false, sizeConsent: false, website: "", turnstile_token: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [success, setSuccess] = useState(false);

  if (!open) return null;

  const isSmall = requestMode === "small";
  const seg = setData.segments;
  const preview = [seg.TOP[idx.TOP], seg.MIDDLE[idx.MIDDLE], seg.BOTTOM[idx.BOTTOM]];
  const rawPrice = setData.set.price_label?.trim();
  const displayPrice = rawPrice
    ? (/€|EUR/i.test(rawPrice) ? rawPrice : `${rawPrice} €`)
    : "wird persönlich vereinbart";
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const reset = () => setForm({
    name: "", email: "", phone: "", message: "", consent: false, sizeConsent: false, website: "", turnstile_token: "",
  });

  const close = () => {
    setFeedback("");
    setSuccess(false);
    reset();
    onClose();
  };

  const submit = async (e) => {
    e.preventDefault();
    setFeedback("");
    if (!form.name.trim() || !form.email.trim()) {
      const message = "Bitte Name und E-Mail ausfüllen.";
      setFeedback(message);
      toast.error(message);
      return;
    }
    if (!form.sizeConsent) {
      setFeedback("Bitte die Größen-Info bestätigen.");
      toast.error("Bitte die Größen-Info bestätigen.");
      return;
    }
    if (!form.consent) {
      setFeedback("Bitte dem Datenschutz zustimmen.");
      toast.error("Bitte dem Datenschutz zustimmen.");
      return;
    }
    if (!selectedSlots?.top || !selectedSlots?.middle || !selectedSlots?.bottom) {
      setFeedback("Die Kombination konnte nicht eindeutig bestimmt werden.");
      toast.error("Die Kombination konnte nicht eindeutig bestimmt werden.");
      return;
    }

    const requestMessage = isSmall
      ? `[KLEINE VARIANTE · ${SMALL_PRICE_RANGE}]\nAnfrage für eine kleine, bewusst reduzierte Tattoo-Version dieser Auswahl. Preisrahmen laut Website: ${SMALL_PRICE_RANGE}, abhängig von Motiv, Größe, Körperstelle und Aufwand.${form.message.trim() ? `\n\n${form.message.trim()}` : ""}`
      : form.message;

    setSubmitting(true);
    try {
      await api.post("/public/request", {
        set_id: setData.set.id,
        top: selectedSlots.top,
        middle: selectedSlots.middle,
        bottom: selectedSlots.bottom,
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: requestMessage,
        placement: DEFAULT_PLACEMENT,
        desired_size: isSmall ? SMALL_SIZE : DEFAULT_SIZE,
        consent: form.consent,
        website: form.website,
        turnstile_token: form.turnstile_token,
      });
      setSuccess(true);
      setFeedback("");
      toast.success("Anfrage gesendet! Ich melde mich bei dir.");
      onSuccess?.();
      reset();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const message = typeof d === "string" ? d : "Die Anfrage konnte nicht gesendet werden. Bitte versuche es noch einmal.";
      setFeedback(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="request-modal-backdrop" onClick={close}>
      <div className="request-modal-card" onClick={(e) => e.stopPropagation()} data-testid="tattoo-request-modal">
        <div className="request-modal-header">
          <div>
            <div className="eyebrow">Deine Kombination</div>
            <h2>{isSmall ? "Kleine Tattoo-Variante anfragen" : "Dieses Tattoo anfragen"}</h2>
          </div>
          <button onClick={close} aria-label="Schließen" data-testid="request-close" className="request-close-button">
            <X size={21} />
          </button>
        </div>

        <div className="request-modal-body">
          {success ? (
            <div className="request-success" role="status" aria-live="polite">
              <CheckCircle2 size={52} />
              <div className="eyebrow">Anfrage übermittelt</div>
              <h3>Danke – deine Tattoo-Anfrage ist angekommen.</h3>
              <p>Die Kombination wurde reserviert. René meldet sich persönlich bei dir.</p>
              <button type="button" className="request-submit-button" onClick={close}>Fenster schließen</button>
            </div>
          ) : (
          <>
          {isSmall ? (
            <div
              className="request-preview-card"
              style={{
                alignItems: "stretch",
                gap: 18,
                padding: 14,
                borderColor: "rgba(214,79,120,.32)",
                background: "linear-gradient(180deg, rgba(214,79,120,.06), rgba(255,255,255,.025))",
              }}
            >
              <div style={{ minWidth: 0, flex: "0 1 46%" }}>
                <img
                  src={SMALL_EXAMPLE_SRC}
                  alt="Beispiele für kleine, bewusst reduzierte Tattoo-Varianten"
                  loading="lazy"
                  style={{
                    display: "block",
                    width: "100%",
                    maxHeight: 330,
                    objectFit: "contain",
                    border: "1px solid rgba(255,255,255,.12)",
                    background: "#b8b4ae",
                  }}
                />
              </div>
              <div style={{ flex: "1 1 260px", alignSelf: "center" }}>
                <div className="eyebrow">Small Tattoo</div>
                <strong style={{ display: "block", marginTop: 5, fontSize: 18 }}>Aus der Auswahl kann auch eine kleine Variante entstehen.</strong>
                <p style={{ marginBottom: 8 }}>
                  Dafür reduziere ich das Motiv bewusst: weniger feine Details, klarere Flächen und eine kompaktere Form. Nicht jede Kombination lässt sich gleich stark verkleinern – entscheidend ist, dass das Tattoo auch in kleiner Größe sauber lesbar bleibt.
                </p>
                <div style={{ fontWeight: 800, fontSize: 18, color: "#efa1b8" }}>ca. {SMALL_PRICE_RANGE}</div>
                <small style={{ display: "block", marginTop: 4, lineHeight: 1.45, opacity: .78 }}>
                  Je nach Motiv, Größe, Körperstelle und tatsächlichem Ausarbeitungsaufwand. Den konkreten Preis bekommst du vor dem Termin.
                </small>
              </div>
            </div>
          ) : null}

          <div className="request-preview-card">
            <div className="request-preview-image">
              {preview.map((p, i) => (
                <img key={i} src={resolveUrl(p?.asset_url)} alt={p?.name || `Segment ${i + 1}`} />
              ))}
            </div>
            <div>
              <div className="request-preview-number">Nr. {String(combo?.number || 0).padStart(3, "0")}</div>
              <strong>{setData.set.name}</strong>
              <p>
                {isSmall
                  ? "Das ist deine aktuelle Ausgangsauswahl. Für die kleine Version übernehme ich nicht einfach alle Details, sondern entwickle daraus eine reduzierte, tattoo-taugliche Variante."
                  : "Die Kombination ist der Ausgangspunkt. Daraus entsteht anschließend eine eigenständige, tattoo-taugliche Ausarbeitung für Körperstelle, Größe und gewünschten Stil."}
              </p>
            </div>
          </div>

          <div className="request-size-note">
            <strong>Preis &amp; Größe</strong>
            <p>
              <b>{isSmall ? `Preisrahmen: ${SMALL_PRICE_RANGE}.` : `Preisrahmen: ${displayPrice}.`}</b>{" "}
              {isSmall
                ? "Der Rahmen gilt für bewusst reduzierte Small-Tattoo-Versionen. Der konkrete Preis richtet sich nach Motiv, sinnvoller Mindestgröße, Körperstelle und tatsächlichem Aufwand und wird vor dem Termin bestätigt."
                : "Der endgültige Preis richtet sich nach Größe, Körperstelle und tatsächlichem Aufwand und wird vor dem Termin gemeinsam vereinbart. Für eine klare Wirkung sind diese Motive mindestens handflächengroß gedacht."}
            </p>
          </div>

          <form onSubmit={submit} className="request-form" noValidate>
            <input
              type="text" tabIndex={-1} autoComplete="off"
              value={form.website} onChange={(e) => set("website", e.target.value)}
              style={{ position: "absolute", left: "-9999px" }} aria-hidden="true"
            />

            <div className="request-field-grid">
              <div className="request-field">
                <Label>Name *</Label>
                <Input data-testid="request-input-name" value={form.name} onChange={(e) => set("name", e.target.value)} />
              </div>
              <div className="request-field">
                <Label>E-Mail *</Label>
                <Input data-testid="request-input-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} />
              </div>
            </div>

            <div className="request-field">
              <Label>Telefon <span>(optional)</span></Label>
              <Input data-testid="request-input-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
            </div>

            <div className="request-field">
              <Label>Nachricht <span>(optional)</span></Label>
              <Textarea
                data-testid="request-input-message"
                value={form.message}
                onChange={(e) => set("message", e.target.value)}
                rows={3}
                placeholder={isSmall
                  ? "Wenn du schon weißt, wo die kleine Version hin soll oder welche Elemente unbedingt bleiben sollen, schreib es mir hier."
                  : "Wenn du schon weißt, wo das Tattoo hin soll oder was dir an der Kombination besonders gefällt, schreib es mir hier."}
              />
            </div>

            <label className="request-consent request-consent-emphasis">
              <Checkbox data-testid="request-checkbox-size" checked={form.sizeConsent} onCheckedChange={(v) => set("sizeConsent", !!v)} />
              <span>
                {isSmall
                  ? `Ich habe verstanden, dass die kleine Version bewusst vereinfacht wird, der Preisrahmen bei ${SMALL_PRICE_RANGE} liegt und Größe sowie konkreter Preis vor dem Termin abgestimmt werden.`
                  : "Ich habe verstanden, dass dieses Motiv mindestens handflächengroß gedacht ist und die endgültige Größe gemeinsam abgestimmt wird."}
              </span>
            </label>

            <label className="request-consent">
              <Checkbox data-testid="request-checkbox-consent" checked={form.consent} onCheckedChange={(v) => set("consent", !!v)} />
              <span>
                Ich stimme zu, dass meine Angaben zur Bearbeitung der Anfrage gespeichert werden.{" "}
                <a href="https://neu.artpart.at/datenschutz" target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                  Datenschutz
                </a>
              </span>
            </label>

            <TurnstileWidget onToken={(token) => set("turnstile_token", token)} />

            {feedback ? <div className="request-form-feedback" role="alert">{feedback}</div> : null}

            <button type="submit" disabled={submitting} data-testid="request-submit-button" className="request-submit-button">
              {submitting ? "Sende…" : isSmall ? "Kleine Variante anfragen" : "Tattoo anfragen"}
            </button>
          </form>
          </>
          )}
        </div>
      </div>
    </div>
  );
}
